/**
 * useFeed Hook
 * 
 * Custom hook for managing feed data with infinite scroll using TanStack Query
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { FeedApiService } from './feedApiService';
import { FeedItem } from './types';

const PER_PAGE = 10;

interface UseFeedOptions {
    categoryId?: number;
    jobLevelId?: number;
    authorName?: string;
}

export const useFeed = (options: UseFeedOptions = {}) => {
    const { categoryId, jobLevelId, authorName } = options;

    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status,
        refetch
    } = useInfiniteQuery({
        queryKey: ['feed', { categoryId, jobLevelId, authorName }],
        queryFn: async ({ pageParam = 1 }) => {
            const response = await FeedApiService.fetchFeed({
                page: pageParam,
                per_page: PER_PAGE,
                category_id: categoryId,
                job_level_id: jobLevelId,
                author_name: authorName,
            });
            return response;
        },
        getNextPageParam: (lastPage) => {
            // Use has_next_page from API response
            return lastPage.has_next_page ? lastPage.page + 1 : undefined;
        },
        initialPageParam: 1,
        select: (data) => ({
            pages: data.pages,
            pageParams: data.pageParams,
            feedItems: data.pages.flatMap(page => page.items),
        }),
    });

    return {
        feedItems: data?.feedItems ?? [],
        error,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status,
        refetch,
    };
};